import type { StatsToken, StatsTokenCreated, UserOrganization } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChartNoAxesColumn, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { CopyButton } from '@/components/copy-button';
import { ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { relativeTime } from '@/lib/format';

/**
 * The organization's read-only tokens for `GET /v1/stats/errors`, which an
 * internal service such as a status dashboard polls for error counts.
 *
 * Admins and owners only, and shown only to them. Unlike an upload token, a
 * stats token belongs to the organization: every admin sees and may revoke
 * every one. It still names the admin who created it and works only while they
 * are an admin here, so the list flags the ones that have stopped working for
 * that reason. The secret is shown once, when the token is created.
 */
export function StatsTokens({ organization }: { organization: UserOrganization }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [created, setCreated] = useState<StatsTokenCreated | null>(null);
  const [revoking, setRevoking] = useState<StatsToken | null>(null);

  const tokens = useQuery({
    queryKey: ['stats-tokens', organization.id],
    queryFn: () => client.listStatsTokens(organization.id),
  });
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['stats-tokens', organization.id] });

  const revoke = useMutation({
    mutationFn: (tokenId: string) => client.revokeStatsToken(organization.id, tokenId),
    onSuccess: (_, tokenId) => {
      // A secret on screen for a token that no longer works would only mislead.
      if (created?.stats_token.id === tokenId) setCreated(null);
      setRevoking(null);
      invalidate();
    },
  });

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <ChartNoAxesColumn className="size-4" />
            Stats tokens
          </CardTitle>
          <CardDescription>
            For a service that charts {organization.name}&apos;s errors. A stats token reads hourly
            event counts and open issue counts for every project here, and nothing else: not an
            issue, not an event, not a member. It works only while the admin who created it is still
            an admin here. Every admin sees and can revoke every stats token.
          </CardDescription>
        </div>
        <CreateTokenDialog
          organization={organization}
          onCreated={(result) => {
            setCreated(result);
            invalidate();
          }}
        />
      </CardHeader>
      <CardContent className="space-y-3">
        {created && <CreatedSecret created={created} onDismiss={() => setCreated(null)} />}

        {tokens.isLoading && <ListSkeleton rows={2} />}
        {tokens.isError && (
          <ErrorState error={tokens.error} onRetry={() => void tokens.refetch()} />
        )}
        {tokens.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">This organization has no stats tokens.</p>
        )}

        {tokens.data?.map((token) => (
          <TokenRow key={token.id} token={token} onRevoke={() => setRevoking(token)} />
        ))}
      </CardContent>

      <Dialog
        open={revoking !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRevoking(null);
            revoke.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke {revoking?.name}?</DialogTitle>
            <DialogDescription>
              Anything still using it can no longer read {organization.name}&apos;s error counts.
              This cannot be undone; create a new token to replace it.
            </DialogDescription>
          </DialogHeader>
          {revoke.error && (
            <p className="text-sm text-destructive">{(revoke.error as Error).message}</p>
          )}
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={() => revoking && revoke.mutate(revoking.id)}
              disabled={revoke.isPending}
            >
              Revoke token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function TokenRow({ token, onRevoke }: { token: StatsToken; onRevoke: () => void }) {
  const expired = token.expires_at !== null && new Date(token.expires_at).getTime() <= Date.now();
  const creator = token.created_by?.name ?? 'a deleted account';

  return (
    <div className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="min-w-0 text-sm font-medium wrap-anywhere">{token.name}</span>
          <code className="text-xs text-muted-foreground">{token.token_prefix}…</code>
          {expired && <Badge variant="muted">expired</Badge>}
          {!token.active && (
            <Badge
              variant="destructive"
              title="The person who created it is no longer an admin here. Replace it with a token created by someone who is."
            >
              not working
            </Badge>
          )}
        </div>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          created by {creator} {relativeTime(token.created_at)} ago
          {token.last_used_at
            ? ` · last used ${relativeTime(token.last_used_at)} ago`
            : ' · never used'}
          {token.expires_at
            ? ` · ${expired ? 'expired' : 'expires'} ${new Date(token.expires_at).toLocaleDateString()}`
            : ' · never expires'}
        </span>
      </div>
      <Button variant="ghost" size="icon" title={`Revoke ${token.name}`} onClick={onRevoke}>
        <Trash2 />
        <span className="sr-only">Revoke {token.name}</span>
      </Button>
    </div>
  );
}

/** The secret of a token just created, which the server cannot show again. */
function CreatedSecret({
  created,
  onDismiss,
}: {
  created: StatsTokenCreated;
  onDismiss: () => void;
}) {
  return (
    <div className="space-y-2 rounded-md border border-primary bg-brand-subtle p-3">
      <p className="text-sm">
        Created <span className="font-medium">{created.stats_token.name}</span>. Give it to the
        service as a bearer token for <code>GET /v1/stats/errors</code>:
      </p>
      <div className="flex items-center gap-2">
        <Input
          readOnly
          value={created.secret}
          onFocus={(event) => event.currentTarget.select()}
          className="font-mono text-xs"
          aria-label="Stats token"
        />
        <CopyButton value={created.secret} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Copy it now: you won&apos;t see this token again. If you lose it, revoke it and create
          another.
        </p>
        <Button variant="outline" size="sm" onClick={onDismiss}>
          Done
        </Button>
      </div>
    </div>
  );
}

/** Radix needs a value for every option, and the empty string is not one. */
const NEVER = 'never';
const EXPIRY_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
  { value: NEVER, label: 'Never' },
];

function CreateTokenDialog({
  organization,
  onCreated,
}: {
  organization: UserOrganization;
  onCreated: (result: StatsTokenCreated) => void;
}) {
  const { client } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState('365');

  const create = useMutation({
    mutationFn: () =>
      client.createStatsToken(organization.id, {
        name: name.trim(),
        expires_in_days: expiry === NEVER ? null : Number(expiry),
      }),
    onSuccess: (result) => {
      onCreated(result);
      setOpen(false);
      setName('');
      setExpiry('365');
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) create.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus />
          Create token
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a stats token</DialogTitle>
          <DialogDescription>
            Name it after the service that will use it, so you know what breaks if you revoke it.
          </DialogDescription>
        </DialogHeader>

        <form
          id="stats-token-form"
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="stats-token-name">Name</Label>
            <Input
              id="stats-token-name"
              required
              autoComplete="off"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Peaky Dashboard"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="stats-token-expiry">Expires after</Label>
            <Select value={expiry} onValueChange={setExpiry}>
              <SelectTrigger id="stats-token-expiry" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPIRY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {create.error && (
            <p className="text-sm text-destructive">{(create.error as Error).message}</p>
          )}
        </form>

        <DialogFooter>
          <Button type="submit" form="stats-token-form" disabled={!name.trim() || create.isPending}>
            Create token
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
